import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { SHARED_ENVIRONMENT } from '@k12/environment';

/**
 * Request payload for generating a Metabase guest embed token.
 */
export interface MetabaseEmbedTokenRequest {
  /** Type of Metabase entity: 'Question' or 'Dashboard' */
  entityType: 'Question' | 'Dashboard';

  /** ID of the Metabase question or dashboard */
  entityId: number;

  /** Optional locked parameters that restrict data and cannot be changed by users */
  lockedParameters?: Record<string, unknown>;

  /** Optional token expiration in minutes (defaults to 10 minutes) */
  expirationMinutes?: number;
}

/**
 * Response containing the JWT token for Metabase guest embeds.
 */
export interface MetabaseEmbedTokenResponse {
  /** The signed JWT token to pass to the Metabase embed component */
  jwt: string;
}

/**
 * Cached token with metadata for expiration management.
 */
interface CachedToken {
  jwt: string;
  expiresAt: Date;
}

/**
 * Service for managing Metabase guest embed JWT tokens.
 * Handles token generation, caching, and automatic refresh.
 */
@Injectable({
  providedIn: 'root'
})
export class MetabaseService {
  private readonly http = inject(HttpClient);
  private readonly tokenCache = new Map<string, CachedToken>();
  private readonly sharedEnvironment = inject(SHARED_ENVIRONMENT);
  private readonly apiUrl = this.sharedEnvironment.adminApiUrl;

  /** Cache tokens for 9 minutes if they have 10-minute expiry (leave 1 minute buffer for refresh) */
  private readonly CACHE_DURATION_MS = 9 * 60 * 1000;

  /**
   * Gets or generates a JWT token for embedding a Metabase question or dashboard.
   * Tokens are cached and automatically refreshed before expiration.
   *
   * @param entityType Type of entity ('Question' or 'Dashboard')
   * @param entityId ID of the question or dashboard
   * @param lockedParameters Optional parameters to lock for data security
   * @param expirationMinutes Optional token expiration (defaults to 10 minutes)
   * @returns Observable of the JWT token string
   */
  getEmbedToken(
    entityType: 'Question' | 'Dashboard',
    entityId: number,
    lockedParameters?: Record<string, unknown>
  ): Observable<string> {
    const cacheKey = this.getCacheKey(entityType, entityId, lockedParameters);

    // Check cache first
    const cached = this.tokenCache.get(cacheKey);
    if (cached && new Date() < cached.expiresAt) {
      console.log(`[MetabaseService] Using cached token for ${entityType} ${entityId}`);
      return of(cached.jwt);
    }

    // Generate new token
    console.log(`[MetabaseService] Generating new token for ${entityType} ${entityId}`);
    return this.generateToken(entityType, entityId, lockedParameters).pipe(
      tap((jwt) => this.cacheToken(cacheKey, jwt)),
      catchError((error) => {
        console.error('[MetabaseService] Error generating token:', error);
        // If token generation fails, remove from cache to force retry next time
        this.tokenCache.delete(cacheKey);
        return throwError(() => new Error('Failed to generate Metabase embed token'));
      })
    );
  }

  /**
   * Generates a new JWT token from the backend API.
   */
  private generateToken(
    entityType: 'Question' | 'Dashboard',
    entityId: number,
    lockedParameters?: Record<string, unknown>,
    expirationMinutes?: number
  ): Observable<string> {

    const url = this.apiUrl + '/metabase/embed-token';

    const request: MetabaseEmbedTokenRequest = {
      entityType,
      entityId,
      lockedParameters,
      expirationMinutes
    };

    return this.http.post<MetabaseEmbedTokenResponse>(url, request)
      .pipe(map((response) => response.jwt));
  }

  /**
   * Caches a token with expiration metadata.
   */
  private cacheToken(cacheKey: string, jwt: string): void {
    const expiresAt = new Date(Date.now() + this.CACHE_DURATION_MS);
    this.tokenCache.set(cacheKey, { jwt, expiresAt });
    console.log(`[MetabaseService] Cached token until ${expiresAt.toISOString()}`);
  }

  /**
   * Generates a cache key from request parameters.
   */
  private getCacheKey(entityType: string, entityId: number, lockedParameters?: Record<string, unknown>): string {
    const paramsKey = lockedParameters ? JSON.stringify(lockedParameters) : '';
    return `${entityType}-${entityId}-${paramsKey}`;
  }

  /**
   * Clears the token cache. Useful for testing or forcing token refresh.
   */
  clearCache(): void {
    this.tokenCache.clear();
    console.log('[MetabaseService] Token cache cleared');
  }

  /**
   * Removes a specific token from the cache.
   */
  clearTokenFromCache(entityType: string, entityId: number, lockedParameters?: Record<string, unknown>): void {
    const cacheKey = this.getCacheKey(entityType, entityId, lockedParameters);
    this.tokenCache.delete(cacheKey);
    console.log(`[MetabaseService] Removed ${cacheKey} from cache`);
  }
}
